import { create } from 'zustand'
import {
  testIsSteppedUp,
  initiateStepUpAuth,
  submitStepUpChallenge,
} from '@/routes/shared/_api'
import { Auth } from '@aws-amplify/auth'
import { maskPhoneNumberCognito } from '@/routes/shared/utils'

type StepUpStore = {
  isSteppedUp: boolean
  hasCheckedStepUp: boolean
  stepUpType: string
  stepUpCode: string
  showStepUpDialog: boolean
  maskedPhoneNumber: string
  isSubmitting: boolean
  setStepUpCode: (code: string) => void
  checkStepUpStatus: () => Promise<boolean>
  startStepUpAuth: () => Promise<boolean>
  submitStepUpAuth: () => Promise<boolean>
  closeStepUpDialog: () => void
  reset: () => void
}

export const useStepUpStore = create<StepUpStore>((set, get) => ({
  isSteppedUp: false,
  hasCheckedStepUp: false,
  stepUpType: '',
  stepUpCode: '',
  showStepUpDialog: false,
  maskedPhoneNumber: '',
  isSubmitting: false,

  setStepUpCode: (code) => set({ stepUpCode: code }),

  checkStepUpStatus: async () => {
    try {
      await testIsSteppedUp()
      set({ isSteppedUp: true })
      return true
    } catch (error: any) {
      if (error?.status === 401) {
        set({ isSteppedUp: false })
        return false
      }
      // For other errors, assume we need step up
      set({ isSteppedUp: false })
      return false
    } finally {
      set({ hasCheckedStepUp: true })
    }
  },

  startStepUpAuth: async () => {
    try {
      const initiateResponse = await initiateStepUpAuth()

      if (initiateResponse.code) {
        set({ stepUpType: initiateResponse.code })
        if (initiateResponse.code === 'SMS_STEP_UP') {
          const user = await Auth.currentAuthenticatedUser()
          set({ maskedPhoneNumber: maskPhoneNumberCognito(user) })
        }
        set({ showStepUpDialog: true })
        return true
      }
      return false
    } catch (error) {
      console.error('Failed to initiate step-up auth:', error)
      return false
    }
  },

  submitStepUpAuth: async () => {
    const { stepUpType, stepUpCode } = get()
    set({ isSubmitting: true })

    try {
      const stepUpResponse = await submitStepUpChallenge(stepUpType, stepUpCode)

      if (
        stepUpResponse.name === 'Status' &&
        stepUpResponse.details === 'Challenge Successful'
      ) {
        set({ isSteppedUp: true })
        return true
      }
      return false
    } finally {
      set({ isSubmitting: false })
    }
  },

  closeStepUpDialog: () => {
    set({
      showStepUpDialog: false,
      stepUpCode: '',
      stepUpType: '',
      maskedPhoneNumber: '',
    })
  },

  reset: () =>
    set({
      isSteppedUp: false,
      hasCheckedStepUp: false,
      stepUpType: '',
      stepUpCode: '',
      showStepUpDialog: false,
      maskedPhoneNumber: '',
      isSubmitting: false,
    }),
}))
